import { getStandardButtonStyles } from "../../styles/standardButtons";
import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import {
    makeStyles,
    Button,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    CircularProgress,
    Grid,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    DialogContentText
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { green } from "@material-ui/core/colors";
import { Stack } from "@mui/material";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { getKanbanLaneOrder } from "../../services/companyKanbanService";

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    textField: {
        marginRight: theme.spacing(1),
        flex: 1,
    },
    btnWrapper: {
        position: "relative",
    },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
        width: "100%"
    },
}));

const FlowBuilderAddKanbanModal = ({ open, onSave, data, onUpdate, close, whatsappIds, flowKanbanId, flowBrandId }) => {
    const classes = useStyles();
    const isMounted = useRef(true);

    const [activeModal, setActiveModal] = useState(false);
    const [loading, setLoading] = useState(false);

    // Data States
    const [boards, setBoards] = useState([]);
    const [lanes, setLanes] = useState([]);
    const [allTags, setAllTags] = useState([]); // Store all fetchable tags
    const [availableTags, setAvailableTags] = useState([]); // Displayed options

    // State for Confirmation Dialog
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState(null); // { type: 'board'|'lane', value: any }

    // Selection States
    const [name, setName] = useState("");
    const [selectedBoard, setSelectedBoard] = useState("");
    const [selectedLane, setSelectedLane] = useState("");
    const [selectedLaneTags, setSelectedLaneTags] = useState([]);

    const loadLanes = async (boardId) => {
        if (!boardId) return [];
        try {
            const { data: kanbanData } = await api.get("/tag/kanban/", {
                params: { kanbanBoardId: boardId }
            });
            const laneList = kanbanData.lista || [];

            let orderedLanes = laneList;
            try {
                const laneOrder = await getKanbanLaneOrder(boardId);
                if (laneOrder && laneOrder.length > 0) {
                    const laneMap = new Map(laneList.map(l => [String(l.id), l]));
                    const sorted = [];

                    laneOrder.forEach(id => {
                        const strId = String(id);
                        if (laneMap.has(strId)) {
                            sorted.push(laneMap.get(strId));
                            laneMap.delete(strId);
                        }
                    });

                    laneMap.forEach(l => sorted.push(l));
                    orderedLanes = sorted;
                } else {
                    orderedLanes = laneList.sort((a, b) => a.id - b.id);
                }
            } catch (e) {
                console.error("Failed to sort lanes", e);
                orderedLanes = laneList.sort((a, b) => a.id - b.id);
            }
            return orderedLanes;
        } catch (err) {
            console.error("Failed to fetch lanes", err);
            return [];
        }
    };

    useEffect(() => {
        if (open === "edit") {
            loadInitialData(data?.data);
            setActiveModal(true);
        } else if (open === "create") {
            // Reset immediately to avoid stale data
            setName("");
            setSelectedBoard("");
            setSelectedLane("");
            setLanes([]);
            setSelectedLaneTags([]);
            setAvailableTags([]);

            loadInitialData(null);
            setActiveModal(true);
        }
        return () => {
            isMounted.current = false;
        };
    }, [open, whatsappIds, flowKanbanId, flowBrandId]);

    const loadInitialData = async (nodeData) => {
        setLoading(true);
        const t = new Date().getTime();

        try {
            // 1. Fetch Boards
            const { data: boardList } = await api.get("/kanban-boards", {
                params: {
                    _t: t
                }
            });

            let filteredBoards = Array.isArray(boardList) ? boardList : [];
            // Si el flujo se creó dentro de un embudo (flowKanbanId), SOLO mostrar los tableros de ese embudo
            if (flowKanbanId) {
                filteredBoards = filteredBoards.filter(b => Number(b.id) === Number(flowKanbanId));
            } else if (flowBrandId) {
                // Si se creó sin embudo pero tiene marca, mostrar tableros de esa marca
                filteredBoards = filteredBoards.filter(b => Number(b.brandId) === Number(flowBrandId));
            }

            console.log("[FlowBuilderAddKanbanModal] Filtered boards:", filteredBoards);
            setBoards(filteredBoards);

            // 2. Fetch Available Tags (Non-Kanban and Non-System)
            const { data: tagList } = await api.get("/tags/list", { params: { kanban: 0, tagsystem: 0, limit: "-1" } });

            setAllTags(tagList);
            setAvailableTags(tagList); // Default to all if no lane selected

            if (nodeData) {
                setName(nodeData.name || "");

                // Determine Board: If flow has specific embudo, enforce that embudo's board
                let boardId = flowKanbanId ? Number(flowKanbanId) : nodeData.selectedBoard?.id;
                if (!boardId && filteredBoards.length === 1) {
                    boardId = filteredBoards[0].id;
                }

                if (boardId) {
                    setSelectedBoard(boardId);
                    const orderedLanes = await loadLanes(boardId);
                    setLanes(orderedLanes);

                    // Restore Lane
                    if (nodeData.selectedLane?.id) {
                        const targetLaneId = nodeData.selectedLane.id;
                        setSelectedLane(targetLaneId);

                        // Filter tags based on restored lane
                        const selectedLaneObj = orderedLanes.find(l => l.id == targetLaneId);
                        if (selectedLaneObj) {
                            console.log("[FlowBuilder] Restoring Lane:", selectedLaneObj.name, selectedLaneObj);
                            if (selectedLaneObj.kanbanLaneTags && selectedLaneObj.kanbanLaneTags.length > 0) {
                                const laneSpecificTags = selectedLaneObj.kanbanLaneTags.map(klt => klt.tag).filter(t => t);
                                setAvailableTags(laneSpecificTags);
                            } else {
                                setAvailableTags([]); // Strict: No tags configured -> No options
                            }
                        } else {
                            setAvailableTags([]);
                        }
                    }
                }

                // Restore Selected Tags
                if (nodeData.laneTags) {
                    setSelectedLaneTags(nodeData.laneTags);
                }
            } else {
                // Modo creación: si el flujo pertenece a un embudo, auto-seleccionar ese tablero y cargar sus etapas
                const autoBoardId = flowKanbanId ? Number(flowKanbanId) : (filteredBoards.length === 1 ? filteredBoards[0].id : null);
                if (autoBoardId) {
                    setSelectedBoard(autoBoardId);
                    const orderedLanes = await loadLanes(autoBoardId);
                    setLanes(orderedLanes);
                }
            }

        } catch (err) {
            toastError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleBoardChange = async (boardId) => {
        // If there are tags selected, ask for confirmation
        if (selectedLaneTags.length > 0) {
            setPendingAction({ type: "board", value: boardId });
            setConfirmOpen(true);
            return;
        }
        executeBoardChange(boardId);
    };

    const executeBoardChange = async (boardId) => {
        setSelectedBoard(boardId);
        setSelectedLane(""); // Reset lane when board changes
        setSelectedLaneTags([]); // Clear tags
        setAvailableTags([]);

        if (!boardId) {
            setLanes([]);
            return;
        }
        try {
            const orderedLanes = await loadLanes(boardId);
            setLanes(orderedLanes);
        } catch (err) {
            toastError(err);
        }
    };

    const handleLaneChange = (laneId) => {
        if (selectedLaneTags.length > 0) {
            setPendingAction({ type: "lane", value: laneId });
            setConfirmOpen(true);
            return;
        }
        executeLaneChange(laneId);
    };

    const executeLaneChange = (laneId) => {
        setSelectedLane(laneId);
        setSelectedLaneTags([]); // Clear tags when lane changes

        // Filter Available Tags
        const laneObj = lanes.find(l => l.id == laneId);
        console.log("[FlowBuilder] Selected Lane:", laneId, laneObj);

        if (laneObj && laneObj.kanbanLaneTags && laneObj.kanbanLaneTags.length > 0) {
            const laneSpecificTags = laneObj.kanbanLaneTags.map(klt => klt.tag).filter(t => t);
            setAvailableTags(laneSpecificTags);
        } else {
            // Strict: If no specific tags configured, show NONE to avoid confusion
            setAvailableTags([]);
        }
    };

    const handleConfirmClose = (confirmed) => {
        setConfirmOpen(false);
        if (confirmed && pendingAction) {
            if (pendingAction.type === "board") {
                executeBoardChange(pendingAction.value);
            } else if (pendingAction.type === "lane") {
                executeLaneChange(pendingAction.value);
            }
        }
        setPendingAction(null);
    };

    const handleClose = () => {
        close(null);
        setActiveModal(false);
    };

    const handleSaveContact = () => {
        if (!selectedBoard) {
            return toast.error(i18n.t("flowBuilder.kanban.noBoard") || "Board required");
        }
        if (!selectedLane) {
            return toast.error(i18n.t("flowBuilder.kanban.noLane") || "Lane required");
        }

        const boardObj = boards.find(b => b.id === selectedBoard);
        const laneObj = lanes.find(l => l.id === selectedLane);

        const payload = {
            name: name,
            selectedBoard: boardObj,
            selectedLane: laneObj,
            laneTags: selectedLaneTags
        };

        if (open === "edit") {
            onUpdate({
                ...data,
                data: payload,
            });
        } else if (open === "create") {
            onSave(payload);
        }
        handleClose();
    };

    return (
        <div className={classes.root}>
            <Dialog
                open={activeModal}
                onClose={handleClose}
                fullWidth="md"
                scroll="paper"
                maxWidth="sm"
            >
                <DialogTitle id="form-dialog-title">
                    {open === "create" ? i18n.t("flowBuilder.kanban.addTitle") : i18n.t("flowBuilder.kanban.editTitle")}
                </DialogTitle>
                <Stack>
                    <DialogContent dividers>
                        <Grid container spacing={2}>
                            {/* Name */}
                            <Grid item xs={12}>
                                <TextField
                                    label={i18n.t("flowBuilder.form.name")}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    margin="dense"
                                />
                            </Grid>

                            {/* Board Select */}
                            <Grid item xs={12}>
                                <FormControl variant="outlined" className={classes.formControl} margin="dense">
                                    <InputLabel id="board-select-label">{i18n.t("flowBuilder.kanban.selectBoard")}</InputLabel>
                                    <Select
                                        labelId="board-select-label"
                                        id="board-select"
                                        value={selectedBoard}
                                        onChange={(e) => handleBoardChange(e.target.value)}
                                        label={i18n.t("flowBuilder.kanban.selectBoard")}
                                        fullWidth
                                    >
                                        {!flowKanbanId ? (
                                            <MenuItem value="">
                                                <em>None</em>
                                            </MenuItem>
                                        ) : null}
                                        {boards.map((board) => (
                                            <MenuItem key={board.id} value={board.id}>
                                                {board.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Lane Select */}
                            <Grid item xs={12}>
                                <FormControl variant="outlined" className={classes.formControl} margin="dense" disabled={!selectedBoard}>
                                    <InputLabel id="lane-select-label">{i18n.t("flowBuilder.kanban.selectLane")}</InputLabel>
                                    <Select
                                        labelId="lane-select-label"
                                        id="lane-select"
                                        value={selectedLane}
                                        onChange={(e) => handleLaneChange(e.target.value)}
                                        label={i18n.t("flowBuilder.kanban.selectLane")}
                                        fullWidth
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {lanes.map((lane) => (
                                            <MenuItem key={lane.id} value={lane.id}>
                                                {lane.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Available Tags Multi-Select */}
                            <Grid item xs={12}>
                                <Autocomplete
                                    multiple
                                    id="tags-outlined"
                                    options={availableTags}
                                    getOptionLabel={(option) => option.name}
                                    value={selectedLaneTags}
                                    onChange={(event, newValue) => setSelectedLaneTags(newValue)}
                                    filterSelectedOptions
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            label={i18n.t("flowBuilder.kanban.availableTags")}
                                            placeholder="Tags"
                                        />
                                    )}
                                />
                                <Typography variant="caption" color="textSecondary">
                                    {i18n.t("flowBuilder.kanban.description")}
                                </Typography>
                            </Grid>

                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} className={classes.secondaryButton}>{i18n.t("common.cancel")}</Button>
                        <Button type="submit" onClick={handleSaveContact} disabled={loading} className={classes.applyButton}>{open === "create" ? i18n.t("common.add") : i18n.t("common.save")}
                            {loading && <CircularProgress size={24} className={classes.buttonProgress} />}</Button>
                    </DialogActions>
                </Stack>
            </Dialog>

            {/* Confirmation Dialog for UX Improvement */}
            <Dialog
                open={confirmOpen}
                onClose={() => handleConfirmClose(false)}
            >
                <DialogTitle>{"¿Confirmar cambio?"}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Si cambias el tablero o la etapa, las etiquetas seleccionadas actualmente desaparecerán para poder elegir nuevas. ¿Deseas continuar?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleConfirmClose(false)} className={classes.secondaryButton}>
                        Cancelar</Button>
                    <Button  onClick={() => handleConfirmClose(true)}  className={classes.applyButton} autoFocus>
                        Aceptar</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default FlowBuilderAddKanbanModal;
